import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SearchableActionDropdown } from '@/components/ui/SearchableActionDropdown';
import { SettingsSection } from '@/features/settings/settings-section';
import { IntegrationIcon } from '@/integrations/integration-icon';

import { teamApis } from '../team.query';
import { type IntegrationOption } from '../team.types';

type TeamIntegrationsCardProps = {
  teamId: string;
  allowedIds: string[] | null;
  options: IntegrationOption[];
};

const isMcpServer = (option: IntegrationOption) =>
  option.id.startsWith('mcp:') && option.id !== 'mcp:custom';

const iconKeyOf = (option: IntegrationOption) =>
  option.id.slice(option.id.indexOf(':') + 1);

export const TeamIntegrationsCard = ({
  teamId,
  allowedIds,
  options,
}: TeamIntegrationsCardProps) => {
  const [saving, setSaving] = useState(false);
  const [restricting, setRestricting] = useState(false);
  const restricted = allowedIds !== null || restricting;
  const allowed = options.filter((option) => allowedIds?.includes(option.id));
  const addable = options.filter(
    (option) => !(allowedIds ?? []).includes(option.id)
  );

  const saveAllowedIds = async (ids: string[] | null) => {
    setSaving(true);
    try {
      await teamApis.updateDataControls(teamId, { allowed_integrations: ids });
    } finally {
      setSaving(false);
    }
  };

  const setRestricted = (on: boolean) => {
    setRestricting(on);
    if (!on && allowedIds !== null) void saveAllowedIds(null);
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>Integrations</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="horizontal">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Restrict integrations
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              When on, everyone on the team, admins included, can only connect
              and use the integrations allowed here. Accounts already connected
              are blocked, not disconnected.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={restricted}
            disabled={saving}
            onCheckedChange={setRestricted}
          />
        </SettingsSection.Item>
        {restricted && (
          <SettingsSection.Item direction="vertical">
            <XStack className="w-full items-center justify-between">
              <span className="text-xs font-medium uppercase text-foreground-muted">
                Allowed ({allowed.length})
              </span>
              {allowed.length > 0 && (
                <Button
                  variant="link"
                  size="xs"
                  className="h-auto px-0 text-xs"
                  disabled={saving}
                  onClick={() => void saveAllowedIds([])}
                >
                  Clear all
                </Button>
              )}
            </XStack>
            {allowedIds === null && (
              <span className="pt-2 text-sm text-foreground-muted">
                Add the integrations your team may use. Nothing changes for your
                team until you add the first one.
              </span>
            )}
            {allowedIds !== null && allowed.length === 0 && (
              <span className="pt-2 text-sm text-warning-11">
                Nothing is allowed, so no one on the team can connect or use any
                integration.
              </span>
            )}
            <YStack className="w-full gap-1 pt-2">
              {allowed.map((option) => (
                <XStack
                  key={option.id}
                  className="w-full items-center gap-2 rounded-md px-1 py-1"
                >
                  <IntegrationIcon
                    iconKey={iconKeyOf(option)}
                    iconUrl={option.icon_url}
                    name={option.name}
                    className="size-4"
                  />
                  <span className="flex-1 text-sm">{option.name}</span>
                  {isMcpServer(option) && (
                    <Badge variant="secondary">MCP</Badge>
                  )}
                  <IconButton
                    icon={Icons.cancel}
                    size="xs"
                    variant="ghost"
                    aria-label={`Remove ${option.name}`}
                    disabled={saving}
                    onClick={() =>
                      void saveAllowedIds(
                        (allowedIds ?? []).filter((id) => id !== option.id)
                      )
                    }
                  />
                </XStack>
              ))}
            </YStack>
            <SearchableActionDropdown
              searchPlaceholder="Search integrations"
              emptyResult="No integrations found"
              maxHeight="max-h-72"
              actions={addable.map((option) => ({
                id: option.id,
                label: isMcpServer(option)
                  ? `${option.name} (MCP)`
                  : option.name,
                iconElement: (
                  <IntegrationIcon
                    iconKey={iconKeyOf(option)}
                    iconUrl={option.icon_url}
                    name={option.name}
                    className="size-4"
                  />
                ),
                onSelect: () =>
                  void saveAllowedIds([...(allowedIds ?? []), option.id]),
              }))}
            >
              <Button
                variant="outline"
                size="sm"
                className="mt-2 self-start"
                disabled={saving}
              >
                Add integration
              </Button>
            </SearchableActionDropdown>
          </SettingsSection.Item>
        )}
      </SettingsSection.Group>
    </YStack>
  );
};
